import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const STORAGE_KEY = "cagrinet_cookie_consent";

export default function CookieConsent() {
  const { t, isRtl } = useLang();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        const timer = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const handleAcceptAll = () =>
    save({ necessary: true, analytics: true, marketing: true });

  const handleReject = () =>
    save({ necessary: true, analytics: false, marketing: false });

  const handleSavePrefs = () => save(prefs);

  const categories = [
    { key: "necessary", title: t("cookie.catNecessary"), desc: t("cookie.catNecessaryDesc"), locked: true },
    { key: "analytics", title: t("cookie.catAnalytics"), desc: t("cookie.catAnalyticsDesc"), locked: false },
    { key: "marketing", title: t("cookie.catMarketing"), desc: t("cookie.catMarketingDesc"), locked: false },
  ];

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" />
      <div
        data-testid="cookie-consent"
        className="fixed inset-x-0 bottom-0 z-[70] p-4 sm:p-6"
      >
        <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-[#062B2B] shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-white/10 p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2DD4BF]/20 text-[#2DD4BF]">
                <Cookie className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-white">{t("cookie.title")}</h3>
                <p className="mt-1 text-xs font-medium text-zinc-400">
                  <ShieldCheck className="inline h-3 w-3 me-1 text-[#2DD4BF]" />
                  {t("cookie.poweredBy")}
                </p>
              </div>
            </div>
            <button
              onClick={() => save({ necessary: true, analytics: false, marketing: false })}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
              aria-label={t("cookie.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            <p className="text-sm leading-relaxed text-zinc-300">
              {t("cookie.text")}{" "}
              <Link
                to="/kullanim-politikasi"
                className="font-semibold text-[#2DD4BF] underline-offset-2 hover:underline"
              >
                {t("cookie.policyLink")}
              </Link>
              .
            </p>

            {/* Settings panel */}
            {showSettings && (
              <div className="mt-5 space-y-3" data-testid="cookie-settings">
                {categories.map((cat) => (
                  <label
                    key={cat.key}
                    className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-white/5 p-4"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">{cat.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-zinc-400">{cat.desc}</p>
                    </div>
                    <span className="relative mt-0.5 shrink-0">
                      <input
                        type="checkbox"
                        checked={prefs[cat.key]}
                        disabled={cat.locked}
                        onChange={(e) => setPrefs({ ...prefs, [cat.key]: e.target.checked })}
                        className="peer sr-only"
                      />
                      <span className="block h-6 w-11 cursor-pointer rounded-full bg-zinc-600 transition-colors peer-checked:bg-[#2DD4BF] peer-disabled:cursor-not-allowed peer-disabled:opacity-60" />
                      <span className="absolute top-0.5 block h-5 w-5 rounded-full bg-white shadow transition-all start-0.5 peer-checked:start-[1.375rem]" />
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* Buttons */}
            <div className={`mt-5 flex flex-wrap gap-3 ${showSettings ? "flex-row-reverse" : ""}`}>
              <button
                onClick={handleAcceptAll}
                data-testid="cookie-accept-all"
                className="flex-1 rounded-full bg-[#2DD4BF] px-6 py-3 text-sm font-bold text-[#042E2E] transition-colors hover:bg-[#14B8A6] sm:flex-none"
              >
                {t("cookie.acceptAll")}
              </button>

              {!showSettings ? (
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:flex-none"
                >
                  {t("cookie.settings")}
                </button>
              ) : (
                <button
                  onClick={handleSavePrefs}
                  data-testid="cookie-save"
                  className="flex-1 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:flex-none"
                >
                  {t("cookie.save")}
                </button>
              )}

              <button
                onClick={handleReject}
                data-testid="cookie-reject"
                className="flex-1 rounded-full px-6 py-3 text-sm font-semibold text-zinc-400 transition-colors hover:text-white sm:flex-none"
              >
                {t("cookie.reject")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
